import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';

const baseUrl = 'https://qiqicinema.com';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!movie) {
    return (
      <>
        <SEOHead
          title="页面未找到 - 琪琪影院"
          description="抱歉，您访问的页面不存在。"
          keywords="琪琪影院,404"
          canonicalUrl={`${baseUrl}/article/${id}`}
        />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-200 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 font-heading">页面未找到</h1>
            <p className="text-sm text-foreground-500 mt-2">该影片信息不存在或已下架</p>
            <Link to="/" className="inline-flex items-center gap-2 mt-4 px-5 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = getRelatedMovies(movie.id);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${movie.title} - 琪琪影院高清在线观看`,
        description: movie.description,
        url: `${baseUrl}/article/${movie.id}`,
        author: { '@type': 'Person', name: movie.director },
        datePublished: `${movie.year}-01-01`,
        publisher: { '@type': 'Organization', name: '琪琪影院', url: baseUrl },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: movie.category, item: `${baseUrl}/category/${movie.category}` },
          { '@type': 'ListItem', position: 3, name: movie.title },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${movie.title} - ${movie.year}年高清${movie.category}在线观看 | 琪琪影院`}
        description={`琪琪影院为您提供《${movie.title}》高清在线观看，导演${movie.director}，主演${movie.cast.join('、')}。${movie.description.substring(0, 100)}`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.title}高清,${movie.category},琪琪影院,${movie.director}`}
        canonicalUrl={`${baseUrl}/article/${movie.id}`}
        ogType="article"
        ogImage={movie.image}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18">
        {/* Breadcrumb */}
        <div className="max-w-screen-xl mx-auto px-4 md:px-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: movie.category, href: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />
        </div>

        {/* Article Hero */}
        <section className="relative w-full h-[280px] md:h-[420px] overflow-hidden">
          <img
            src={movie.image}
            alt={`${movie.title} - 琪琪影院高清在线观看`}
            title={`${movie.title} - 琪琪影院高清在线观看`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/30 to-foreground-950/10"></div>
        </section>

        {/* Article Content */}
        <section className="relative -mt-32 md:-mt-40 z-10">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              {/* Main Content */}
              <article className="flex-1 bg-background-50 rounded-lg p-5 md:p-8 border border-background-200/70">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">{movie.category}</span>
                  {movie.type === 'tv' && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700">{movie.type === 'tv' ? '电视剧' : '电影'}</span>
                  )}
                  <span className="text-xs text-foreground-400">{movie.region}</span>
                  <span className="text-xs text-foreground-400">{movie.updateStatus}</span>
                </div>

                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">{movie.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong className="text-accent-600">{movie.rating}</strong>
                  </span>
                  <span>{movie.year}</span>
                  <span>{movie.duration}</span>
                  <span>{movie.type === 'movie' ? '电影' : '电视剧'}</span>
                </div>

                <hr className="my-5 border-background-200" />

                {/* Description */}
                <div>
                  <h2 className="text-base font-semibold text-foreground-950 mb-3 font-heading">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <hr className="my-5 border-background-200" />

                {/* Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-1">导演</h3>
                    <p className="text-sm text-foreground-800">{movie.director}</p>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-1">主演</h3>
                    <p className="text-sm text-foreground-800">{movie.cast.join('、')}</p>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-1">类型</h3>
                    <p className="text-sm text-foreground-800">{movie.category}</p>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-1">地区</h3>
                    <p className="text-sm text-foreground-800">{movie.region}</p>
                  </div>
                </div>

                <hr className="my-5 border-background-200" />

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {[movie.category, movie.type === 'movie' ? '电影' : '电视剧', movie.region, `评分${movie.rating}`, '蓝光画质', '免费观看'].map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>

              {/* Sidebar */}
              <aside className="w-full lg:w-72 shrink-0">
                <div className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4 font-heading">影片信息</h3>
                  <ul className="space-y-3">
                    <li className="flex justify-between text-sm">
                      <span className="text-foreground-500">片名</span>
                      <span className="text-foreground-900 font-medium">{movie.title}</span>
                    </li>
                    <li className="flex justify-between text-sm">
                      <span className="text-foreground-500">年份</span>
                      <span className="text-foreground-900">{movie.year}</span>
                    </li>
                    <li className="flex justify-between text-sm">
                      <span className="text-foreground-500">导演</span>
                      <span className="text-foreground-900">{movie.director}</span>
                    </li>
                    <li className="flex justify-between text-sm">
                      <span className="text-foreground-500">片长</span>
                      <span className="text-foreground-900">{movie.duration}</span>
                    </li>
                    <li className="flex justify-between text-sm">
                      <span className="text-foreground-500">评分</span>
                      <span className="text-accent-600 font-bold">{movie.rating}</span>
                    </li>
                    <li className="flex justify-between text-sm">
                      <span className="text-foreground-500">状态</span>
                      <span className="text-primary-600">{movie.updateStatus}</span>
                    </li>
                  </ul>
                </div>

                {/* Watch Button */}
                <div className="mt-4">
                  <button className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-white font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm">
                    <i className="ri-play-circle-line text-lg"></i>
                    立即观看
                  </button>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Related Movies */}
        {relatedMovies.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-screen-xl mx-auto px-4 md:px-8">
              <h2 className="text-lg font-bold text-foreground-950 font-heading mb-6">
                相关{movie.category}推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4" data-product-shop>
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}